import React, {FC, useCallback, useEffect, useRef, useState} from "react";
import RailsTimeZone from "rails-timezone";
import {fieldsToRails} from "../registrationFieldMapping";

import {
    AdminApiClient,
} from "@getregistered/greg-editors";
import railsFetch from "../railsFetch";
import {useAwarenessUsers} from "../AwarenessUsers/useAwarenessUsers";
import {CollaborativeRegistrationFormEditorProps} from "./types";
import {PrimerEditor} from "./PrimerEditor";
import {FormEditorLoadingState} from "./FormEditorLoadingState";
import {ReadyState, LoadingTimings, DisconnectedError} from "./FormChannelControl";
import {toastManager} from "../DownloadTray/ToastManager";

const formatElapsed = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const disconnectedSubtitle = (elapsed: number): string => {
  const base = "Your edits are preserved locally and will sync when the connection is restored.";
  return elapsed > 0 ? `${base} Disconnected for ${formatElapsed(elapsed)}.` : base;
};

export const CollaborativeFormEditor: FC<CollaborativeRegistrationFormEditorProps> =
  ({
    channelControl: initialChannelControl,
    startDate,
    endDate,
    timezone,
    previewsUrl,
    apiBaseUrl,
    ...props
  }) => {
    const channelControlRef = React.useRef(initialChannelControl);
    const channelControl = channelControlRef.current;
    const apiClient = new AdminApiClient(apiBaseUrl, railsFetch);
    const awarenessUsers = useAwarenessUsers(channelControl.provider);
    const [tracksLastUpdated, setTracksLastUpdated] =
      useState<number | undefined>();
    const [sessionsLastUpdated, setSessionsLastUpdated] =
      useState<number | undefined>();
    const [readyState, setReadyState] = useState<ReadyState>(
      channelControl.readyState
    );
    const [timings, setTimings] = useState<LoadingTimings>(
      channelControl.timings
    );
    const prevReadyStateRef = useRef<ReadyState>(channelControl.readyState);
    const disconnectedToastIdRef = useRef<string | null>(null);
    const disconnectedSinceRef = useRef<number | null>(null);

    // Track disconnection/reconnection via toasts
    useEffect(() => {
      const prev = prevReadyStateRef.current;
      prevReadyStateRef.current = readyState;

      if (readyState === "disconnected") {
        disconnectedSinceRef.current = Date.now();
        const id = toastManager.show({
          message: "Connection lost",
          subtitle: disconnectedSubtitle(0),
          variant: "warning",
          icon: "alert",
          autoCloseMs: 0,
          position: "bottom-right",
        });
        disconnectedToastIdRef.current = id;
      } else if (readyState === "ready" && prev === "disconnected") {
        disconnectedSinceRef.current = null;
        if (disconnectedToastIdRef.current) {
          toastManager.remove(disconnectedToastIdRef.current);
          disconnectedToastIdRef.current = null;
        }
        toastManager.show({
          message: "Connection restored",
          subtitle: "Your edits have been synced.",
          variant: "success",
          icon: "check",
          autoCloseMs: 4000,
        });
      }
    }, [readyState]);

    // Update elapsed time on disconnection toast
    useEffect(() => {
      if (readyState !== "disconnected") return;
      const interval = setInterval(() => {
        const since = disconnectedSinceRef.current;
        const toastId = disconnectedToastIdRef.current;
        if (since && toastId) {
          const elapsed = Math.floor((Date.now() - since) / 1000);
          toastManager.update(toastId, { subtitle: disconnectedSubtitle(elapsed) });
        }
      }, 1000);
      return () => clearInterval(interval);
    }, [readyState]);

    useEffect(() => {
      channelControl.addTrackUpdateListener((value: number) => {
        setTracksLastUpdated(value);
      });
      channelControl.addSessionsUpdateListener((value: number) => {
        setSessionsLastUpdated(value);
      });

      const readyStateListener = (state: ReadyState, newTimings: LoadingTimings) => {
        setReadyState(state);
        setTimings(newTimings);
      };
      channelControl.addReadyStateListener(readyStateListener);

      return () => {
        channelControl.removeReadyStateListener(readyStateListener);
        channelControl.dispose();
      }
    }, [channelControl]);

    const handlePreview = useCallback(
      async (newFields) => {
        const mappedFields = fieldsToRails(newFields);
        const response = await railsFetch(previewsUrl, {
          method: "POST",
          body: JSON.stringify(
            {
              form_preview: { fields: mappedFields },
            },
            (_, v) => (typeof v === "bigint" ? Number(v) : v)
          ),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        const currentPreviewUrl = response.headers.get("Location");
        window.open(currentPreviewUrl, "greg_form_preview");
      },
      [previewsUrl]
    );

    const handleSave = useCallback(() => {
      try {
        channelControl.save();
        toastManager.show({
          message: "Changes Saved",
          variant: "success",
          icon: "check",
          autoCloseMs: 3000,
        });
      } catch (e) {
        if (e instanceof DisconnectedError) {
          toastManager.show({
            message: "Cannot save while disconnected",
            subtitle: "Your edits are preserved locally. Save will be available when the connection is restored.",
            variant: "warning",
            icon: "blocked",
            autoCloseMs: 5000,
            position: "bottom-right",
          });
          return;
        }
        throw e;
      }
    }, [channelControl]);

    const handleReset = useCallback(async () => {
      try {
        await channelControl.reset();
      } catch (e) {
        if (e instanceof DisconnectedError) {
          toastManager.show({
            message: "Cannot reset while disconnected",
            subtitle: "Reset will be available when the connection is restored.",
            variant: "warning",
            icon: "blocked",
            autoCloseMs: 5000,
            position: "bottom-right",
          });
        } else {
          console.error("[FormEditor] Reset failed:", e);
          toastManager.show({
            message: "Reset failed",
            subtitle: e instanceof Error ? e.message : "An unexpected error occurred.",
            variant: "danger",
            icon: "alert",
            autoCloseMs: 5000,
          });
        }
      }
    }, [channelControl]);

    // Show loading state until ready (but not for disconnected — editor works offline)
    if (readyState !== "ready" && readyState !== "disconnected") {
      return <FormEditorLoadingState readyState={readyState} timings={timings} />;
    }

    return <>
      <PrimerEditor apiClient={apiClient}
                         formId={String(channelControl.id)}
                         awarenessUsers={awarenessUsers}
                         ydoc={channelControl.ydoc}
                         currentUser={channelControl.initialAwarenessUser}
                         assistantEvents={channelControl.assistantEvents}
                         startDate={new Date(startDate)}
                         endDate={new Date(endDate)}
                         timezone={
                           timezone !== undefined
                             ? RailsTimeZone.from(timezone)
                             : undefined
                         }
                         tracksLastUpdated={tracksLastUpdated}
                         sessionsLastUpdated={sessionsLastUpdated}
                         {...props}
                         chat={channelControl.assistantChat ? {
                           messages: [],
                           onAsk: channelControl.assistantChat.askAssistant,
                           onTell: channelControl.assistantChat.tellAssistant,
                           onUpdateMessageStatus: channelControl.assistantChat.updateMessageStatus,
                           avatarUrl: channelControl.initialAwarenessUser.avatarUrl,
                         } : undefined}
                         onSave={handleSave}
                         onReset={handleReset}
                         onPreview={handlePreview} />
    </>;
  };
